/**
 * Day-of check-in: tap a name on a phone — checked in; tap again to untick (nothing is removed). A filter by name
 * or apartment; the count at the top is announced. A walk-in is entered first with "Enter an RSVP".
 */
import { useMemo, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventDetail } from '@shared/events';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { errorText } from '@/features/people/api';
import { press, useEventRefresh } from './api';

export function CheckInSheet({ detail, onClose, returnFocusTo }: { detail: EventDetail; onClose: () => void; returnFocusTo: RefObject<HTMLElement | null> }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const refresh = useEventRefresh();
  const e = detail.event;
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [local, setLocal] = useState<Record<string, boolean>>({});
  const coming = useMemo(
    () => detail.rows.filter((r) => r.coming === 'Yes' && (e.rule === 'raffle' && e.raffle?.drawnAt ? r.raffleOutcome === 'Winner' || r.raffleOutcome === 'Standby' : true)),
    [detail.rows, e.rule, e.raffle?.drawnAt],
  );
  const shown = coming.filter((r) => !q.trim() || `${r.name} ${r.unit}`.toLowerCase().includes(q.trim().toLowerCase()));
  const isIn = (id: string, server: boolean) => local[id] ?? server;
  const count = coming.filter((r) => isIn(r.rsvpId, r.checkedIn)).length;

  const tap = async (id: string, name: string, on: boolean) => {
    setBusy(id);
    try {
      await press(e.eventId, { what: 'checkin', rsvpId: id, on });
      setLocal((l) => ({ ...l, [id]: on }));
      announce(on ? t('events.checkin.in', { name }) : t('events.checkin.out', { name }));
      refresh(e.eventId);
    } catch (err) {
      announce(errorText(err), 'assertive');
    } finally {
      setBusy(null);
    }
  };

  return (
    <SheetContent
      wide
      title={t('events.checkin.title', { event: e.name })}
      returnFocusTo={returnFocusTo}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('events.close')}
        </Button>
      }
    >
      <p className="counter" data-testid="checkin-count" aria-live="polite">
        <b className="tnum">{t('events.checkin.count', { n: count, of: coming.length })}</b>
        <span>{t('events.checkin.help')}</span>
      </p>
      <div className="field">
        <label htmlFor="ci-q">{t('events.checkin.filter')}</label>
        <input id="ci-q" className="input" type="search" autoComplete="off" value={q} onChange={(x) => setQ(x.target.value)} />
      </div>
      <ul className="checkin-list" data-testid="checkin-list">
        {shown.map((r) => {
          const on = isIn(r.rsvpId, r.checkedIn);
          return (
            <li key={r.rsvpId}>
              <Button plain type="button" className="checkin-btn" aria-pressed={on} disabled={busy === r.rsvpId} onClick={() => tap(r.rsvpId, r.name, !on)} data-testid={`checkin-${r.rsvpId}`}>
                <Icon name={on ? 'check' : 'people'} />
                <span>
                  <b>{r.staff ? `${r.name} · ${t('staff1102.tag')}` : r.name}</b>
                  <small>
                    {[r.unit, t(`events.type.${r.personType.replace(/\s/g, '')}`), t('events.checkin.party', { count: r.attending }), r.raffleOutcome === 'Standby' ? t('events.row.standby') : '', on ? t('events.checkin.isIn') : t('events.checkin.notIn')].filter(Boolean).join(' · ')}
                  </small>
                </span>
              </Button>
            </li>
          );
        })}
        {!shown.length ? <li className="calm">{t('events.checkin.none')}</li> : null}
      </ul>
    </SheetContent>
  );
}
